import { useFeatureValue } from '@monofront/business/contexts';
import { useMediaQuery } from '@monofront/business/hooks';
import Breadcrumb from '@components/Breadcrumb';
import { useAuthentication } from '@context/businessContainer';
import Arrow from '@monofront/design-system/visuals/Icons/Old/Arrow';
import HomeOutline from '@monofront/design-system/visuals/Icons/Old/HomeOutline';
import Information from '@monofront/design-system/visuals/Icons/Old/Information';
import Button from '@monofront/design-system/components/Button';
import InformativeBox from '@monofront/design-system/components/InformativeBox';
import UserInfos from './UserInfos';
import DesktopSideMenu from './DesktopSideMenu';
import { IShopAccountBody } from '@monofront/types';
import * as React from 'react';
import { LayoutContext } from '@context/LayoutContext';
import { useContext } from 'react';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { isMobileApp } from '@utils/environment';
import { FEATURE_FLAG_ACCOUNT_WARNING } from '@monofront/business/constants';

interface ILayoutAccountProps {
  hasDesktopSideMenu: boolean;
  hasMobileHeader?: boolean;
  hasDesktopUserInfos?: boolean;
  menu?: IShopAccountBody[];
  breadcrumbParent?: {
    href: string;
    label: string;
  };
  children: React.ReactElement<any>;
}

const LayoutAccount: React.FC<ILayoutAccountProps> = ({
  children,
  hasDesktopSideMenu = true,
  hasMobileHeader = false,
  hasDesktopUserInfos = true,
  menu,
  breadcrumbParent,
}) => {
  const { isAuthenticated, customer } = useAuthentication();
  const { mobileTitle } = useContext(LayoutContext);
  const isMobile = useMediaQuery(800);
  const hasUserInfos =
    Boolean(isMobile && hasMobileHeader) ||
    Boolean(!isMobile && hasDesktopUserInfos);
  const router = useRouter();

  const tAccount = useTranslations('Account');
  const tGlobal = useTranslations('Global');

  const isWarningDisplayed = useFeatureValue<{ path: string[] }>(
    FEATURE_FLAG_ACCOUNT_WARNING,
    { path: [] },
  )?.path?.some((path) => path === router.pathname);

  return (
    <div className="limited-width-container">
      <div className="container">
        <div>
          {!isMobile ? (
            <div className="breadcrumb">
              <Breadcrumb>
                <Breadcrumb.Link href={isAuthenticated ? '/home' : '/'}>
                  <HomeOutline
                    color="var(--clr-tertiary-1)"
                    width={24}
                    height={24}
                    role="img"
                    aria-label={tGlobal('goHome')}
                  />
                </Breadcrumb.Link>
                <Breadcrumb.Link href={`/account`}>
                  {tAccount('myAccount')}
                </Breadcrumb.Link>
                {breadcrumbParent && (
                  <Breadcrumb.Link href={breadcrumbParent.href}>
                    {breadcrumbParent.label}
                  </Breadcrumb.Link>
                )}
                {mobileTitle && router.pathname !== '/account' ? (
                  <Breadcrumb.Link>{mobileTitle}</Breadcrumb.Link>
                ) : undefined}
              </Breadcrumb>
            </div>
          ) : null}
          {hasUserInfos && <UserInfos customer={customer} />}
        </div>

        {router.pathname !== '/account' && (
          <div className="mobile-only subheader">
            {!isMobileApp && (
              <Button
                size="medium"
                hasMinWidth={false}
                block={false}
                variant="link"
                onClick={router.back}
                dataTestId="LayoutAccount-Button-go-back"
                className="tw-ml-4"
              >
                <Arrow headDirection="left" width={9} height={15} />
                <span className="back-text">
                  {breadcrumbParent?.label || tAccount('myAccount')}
                </span>
              </Button>
            )}
            <h1 className="title-xxs">{mobileTitle}</h1>
          </div>
        )}

        <div className="content">
          {hasDesktopSideMenu && !isMobile && menu && (
            <div className="menu">
              <DesktopSideMenu menuAccount={menu} />
            </div>
          )}
          <div className={`${hasDesktopSideMenu ? `main-with-menu` : `main`}`}>
            {isWarningDisplayed ? (
              <InformativeBox icon={<Information />}>
                <p>
                  {tGlobal.rich('maintenanceWarning', {
                    b: (children) => <strong>{children}</strong>,
                  })}
                </p>
              </InformativeBox>
            ) : (
              children
            )}
          </div>
        </div>
      </div>
      <style jsx>{`
        .container {
          padding-bottom: var(--space-8);
          position: relative;
        }

        .title {
          text-align: center;
          padding: var(--space-4);
          background-color: var(--white);
          font-size: var(--font-size-3);
          font-weight: var(--font-weight-bold);
        }

        .breadcrumb {
          margin: var(--space-5) 0 var(--space-4) 0;
        }

        .content {
          display: flex;
          width: 100%;
        }

        .menu {
          flex: 1 1 0;
          min-width: 250px;
          position: sticky;
          top: 190px;
          height: 100%;
        }

        .main {
          width: 100%;
        }

        .main-with-menu {
          width: 100%;
          margin: var(--space-5) 0 0 var(--space-5);
          flex: 4 1 0;
        }

        @media (max-width: 800px) {
          .container {
            padding: ${isMobileApp
              ? 'var(--space-5)'
              : 'var(--padding-layout-sm)'};
            padding-bottom: var(--space-3);
          }

          .main-with-menu {
            margin-left: 0;
          }

          .subheader {
            margin-top: var(--space-3);
          }

          .subheader > :global(button) {
            margin-bottom: var(--space-4);
            align-items: center;
            display: flex;
          }

          .back-text {
            font-size: var(--font-size-2);
            margin-left: var(--space-3);
          }
        }
      `}</style>
    </div>
  );
};

export default LayoutAccount;
